import {
    Box, ButtonBase, Chip, CircularProgress, Divider, IconButton,
    List, ListItemButton, MenuItem, Paper, Select, Stack,
    Typography,
} from '@mui/material'
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import AutomaticSheet from 'src/components/sheet/AutomaticSheet'
import { useGetPlanPlanTransportModesFromLonLatToLonLatGet } from 'src/generated/brezavta/queries'
import MyLocationIcon from '@mui/icons-material/MyLocation'
import PlaceIcon from '@mui/icons-material/Place'
import SwapVertIcon from '@mui/icons-material/SwapVert'
import HomeIcon from '@mui/icons-material/Home'
import WorkIcon from '@mui/icons-material/Work'
import SchoolIcon from '@mui/icons-material/School'
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter'
import StarIcon from '@mui/icons-material/Star'
import AddIcon from '@mui/icons-material/Add'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus'
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike'
import ElectricScooterIcon from '@mui/icons-material/ElectricScooter'
import CarRentalIcon from '@mui/icons-material/CarRental'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { DateTime } from 'luxon'
import { formatDuration } from 'src/lib/util'
import PlanDisplay from 'src/components/plan/PlanDisplay'
import { useState } from 'react'
import { Plan, PlanWrapper } from 'src/generated/brezavta/requests/types.gen'
import { POI, loadPois } from 'src/lib/pois'

interface LocationSpec {
    title: string
    latitude: number
    longitude: number
    type: 'GPS' | 'MAP' | 'POI'
}

interface SearchParams {
    from?: LocationSpec
    to?: LocationSpec
    modes?: string[]
    time?: string
    timeMode?: 'ARRIVE' | 'DEPART'
    pageCursor?: string
}

export const Route = createFileRoute('/plan/')({
    component: RouteComponent,
    validateSearch: (search: Record<string, unknown>) => ({
        modes: typeof search.modes === 'string' ? search.modes.split(',') : (search.modes as string[] | undefined),
        from: search.from as LocationSpec | undefined,
        to: search.to as LocationSpec | undefined,
        time: search.time as string | undefined,
        timeMode: search.timeMode as 'ARRIVE' | 'DEPART' | undefined,
        pageCursor: search.pageCursor as string | undefined,
    }),
})

const POI_ICONS: Record<string, React.ReactNode> = {
    home: <HomeIcon sx={{ fontSize: 16 }} />,
    work: <WorkIcon sx={{ fontSize: 16 }} />,
    school: <SchoolIcon sx={{ fontSize: 16 }} />,
    workout: <FitnessCenterIcon sx={{ fontSize: 16 }} />,
    star: <StarIcon sx={{ fontSize: 16 }} />,
}

const MODES = [
    { key: 'TRANSIT', label: 'Transit', Icon: DirectionsBusIcon },
    { key: 'BICYCLE_RENT', label: 'Bike', Icon: DirectionsBikeIcon },
    { key: 'SCOOTER_RENT', label: 'Scooter', Icon: ElectricScooterIcon },
    { key: 'CAR_RENT', label: 'Car', Icon: CarRentalIcon },
]

function RouteComponent() {
    const navigate = useNavigate()
    const { t } = useTranslation()
    const { modes, from, to, time, timeMode, pageCursor } = useSearch({ strict: false }) as SearchParams
    const datetimeStr = (time && time.trim())
        ? time
        : DateTime.now().setZone('Europe/Ljubljana').toFormat('yyyy-MM-dd\'T\'HH:mm')
    const activeModes = modes ?? ['TRANSIT']
    const activeTimeMode = timeMode ?? 'DEPART'

    const [pois] = useState<POI[]>(loadPois)

    const isValid = !!(activeModes.length && from && to)

    const { data: plans, isFetching } = useGetPlanPlanTransportModesFromLonLatToLonLatGet(isValid
        ? {
                path: {
                    from_lon_lat: `${from!.longitude},${from!.latitude}`,
                    to_lon_lat: `${to!.longitude},${to!.latitude}`,
                    transport_modes: activeModes.join(','),
                },
                query: {
                    date: datetimeStr.split('T')[0],
                    time: datetimeStr.split('T')[1].substring(0, 5),
                    type: activeTimeMode,
                    page_cursor: pageCursor ?? '',
                },
            }
        : undefined, undefined, { enabled: isValid })

    const setSearch = (patch: Partial<SearchParams>) =>
        navigate({ search: (prev: unknown) => ({ ...(prev as SearchParams), ...patch }) } as unknown as Parameters<typeof navigate>[0])

    const goToSelect = (selectType: 'from' | 'to') =>
        navigate({
            to: '/plan/select',
            search: { selectType },
            state: { planSearch: { modes, from, to, time } } as Record<string, unknown>,
        })

    const gotoPlanView = (plan: Plan, index: number) =>
        navigate({
            to: '/plan/$mode/$coordinates/$datetime/$type/$page/$itinIndex',
            params: {
                mode: activeModes.join(','),
                coordinates: `${from!.longitude},${from!.latitude};${to!.longitude},${to!.latitude}`,
                datetime: datetimeStr,
                type: activeTimeMode,
                page: 'null',
                itinIndex: String(index),
            },
            state: { plan } as Record<string, unknown>,
        })

    const toggleMode = (key: string) => {
        const next = activeModes.includes(key)
            ? activeModes.filter(m => m !== key)
            : [...activeModes, key]
        if (next.length > 0) setSearch({ modes: next })
    }

    const swapLocations = () => setSearch({ from: to, to: from })

    const setPoiDest = (poi: POI) =>
        setSearch({ to: { title: poi.name, latitude: poi.lat, longitude: poi.lon, type: 'POI' } })

    const goToPage = (cursor: string) => setSearch({ pageCursor: cursor })

    return (
        <AutomaticSheet>
            <Stack sx={{ px: 2, pt: 1, pb: 1, gap: 1.5 }}>

                {/* ── Header ─────────────────────────────────────────────────── */}
                <Typography variant="h6" fontWeight={700}>{t('Where to?')}</Typography>

                {/* ── From / To + POIs ──────────────────────────────────────────── */}
                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <LocationRow
                        icon={<MyLocationIcon sx={{ fontSize: 18, color: 'primary.main' }} />}
                        placeholder={t('Choose starting point')}
                        value={from}
                        onClick={() => goToSelect('from')}
                    />
                    <Divider />
                    <Stack direction="row" alignItems="center">
                        <LocationRow
                            icon={<PlaceIcon sx={{ fontSize: 18, color: 'error.main' }} />}
                            placeholder={t('Choose destination')}
                            value={to}
                            onClick={() => goToSelect('to')}
                            flex
                        />
                        <IconButton
                            size="small"
                            onClick={swapLocations}
                            sx={{ mr: 1, flexShrink: 0, color: 'text.secondary' }}
                        >
                            <SwapVertIcon fontSize="small" />
                        </IconButton>
                    </Stack>
                    {/* POI chips below To */}
                    <Box sx={{ px: 2, py: 1.5, bgcolor: 'action.hover', borderTop: '1px solid', borderColor: 'divider' }}>
                        <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
                            {pois.map(poi => (
                                <Chip
                                    key={poi.id}
                                    icon={<Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5 }}>{POI_ICONS[poi.icon] ?? <StarIcon sx={{ fontSize: 16 }} />}</Box>}
                                    label={poi.name}
                                    size="small"
                                    onClick={() => setPoiDest(poi)}
                                    variant="outlined"
                                />
                            ))}
                            <Chip
                                icon={<AddIcon sx={{ fontSize: '16px' }} />}
                                label={t('Add')}
                                size="small"
                                variant="outlined"
                                onClick={() => navigate({ to: '/plan/poi' } as Record<string, unknown>)}
                                sx={{ borderStyle: 'dashed', color: 'text.secondary' }}
                            />
                        </Stack>
                    </Box>
                </Paper>

                {/* ── Time + modes ────────────────────────────────────────────── */}
                <Paper variant="outlined" sx={{ borderRadius: 2, p: 1.5 }}>
                    <Stack gap={1.25}>
                        {/* Depart / Arrive + datetime */}
                        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                            <Select
                                size="small"
                                variant="outlined"
                                value={activeTimeMode}
                                onChange={e => setSearch({ timeMode: e.target.value as 'DEPART' | 'ARRIVE', pageCursor: undefined })}
                                sx={{ minWidth: 110 }}
                            >
                                <MenuItem value="DEPART">{t('Depart at')}</MenuItem>
                                <MenuItem value="ARRIVE">{t('Arrive by')}</MenuItem>
                            </Select>
                            <Box
                                component="input"
                                type="datetime-local"
                                value={time ?? ''}
                                onChange={e => setSearch({ time: e.target.value })}
                                sx={{
                                    'ml': 'auto', 'border': 'none', 'fontSize': '0.8rem',
                                    'color': 'text.primary', 'bgcolor': 'transparent',
                                    'fontFamily': 'inherit', 'cursor': 'pointer',
                                    '&:focus': { outline: 'none' },
                                }}
                            />
                        </Stack>

                        <Divider />

                        {/* Mode chips - icon only */}
                        <Stack direction="row" gap={0.75} flexWrap="wrap">
                            {MODES.map(({ key, Icon }) => {
                                const active = activeModes.includes(key)
                                return (
                                    <Chip
                                        key={key}
                                        icon={<Icon sx={{ fontSize: '1.1rem' }} />}
                                        size="small"
                                        onClick={() => toggleMode(key)}
                                        color={active ? 'primary' : 'default'}
                                        variant={active ? 'filled' : 'outlined'}
                                        sx={{
                                            '& .MuiChip-label': { display: 'none' },
                                            'borderRadius': '50%',
                                            'width': 36,
                                            'height': 36,
                                            '& .MuiChip-icon': {
                                                margin: 0,
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                            },
                                        }}
                                    />
                                )
                            })}
                        </Stack>
                    </Stack>
                </Paper>
            </Stack>

            {/* ── Plan results with pagination ───────────────────────────────── */}
            <PlanResults
                plans={plans}
                isFetching={isFetching}
                isValid={isValid}
                onSelect={gotoPlanView}
                onPageChange={goToPage}
            />
        </AutomaticSheet>
    )
}

function LocationRow({ icon, placeholder, value, onClick, flex }: {
    icon: React.ReactNode
    placeholder: string
    value?: LocationSpec
    onClick: () => void
    flex?: boolean
}) {
    const { t } = useTranslation()
    const label = value?.type === 'GPS' ? t('Current location') : (value?.title ?? placeholder)
    const isSet = !!value

    return (
        <ButtonBase onClick={onClick} sx={{ width: '100%', textAlign: 'left', flex: flex ? 1 : undefined }}>
            <Stack direction="row" alignItems="center" gap={1.5} sx={{ px: 2, py: 1.5, width: '100%' }}>
                {icon}
                <Typography
                    variant="body2"
                    noWrap
                    sx={{ color: isSet ? 'text.primary' : 'text.disabled', fontWeight: isSet ? 500 : 400 }}
                >
                    {label}
                </Typography>
            </Stack>
        </ButtonBase>
    )
}

function PlanResults({ plans, isFetching, isValid, onSelect, onPageChange }: {
    plans: PlanWrapper | undefined
    isFetching: boolean
    isValid: boolean
    onSelect: (plan: Plan, index: number) => void
    onPageChange: (cursor: string) => void
}) {
    const { t } = useTranslation()
    if (!isValid || !plans) return null

    if (isFetching && !plans) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
            </Box>
        )
    }

    const hasPlans = (plans?.plans?.length ?? 0) > 0

    return (
        <Box>
            {/* Pagination controls at top */}
            {(plans.previous_page || plans.next_page) && (
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2, py: 1 }}>
                    <IconButton
                        size="small"
                        onClick={() => plans.previous_page && onPageChange(plans.previous_page)}
                        disabled={!plans.previous_page}
                    >
                        <ChevronLeftIcon />
                    </IconButton>
                    <Typography variant="caption" color="text.secondary">
                        {!hasPlans && !isFetching ? t('No routes found') : ''}
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={() => plans.next_page && onPageChange(plans.next_page)}
                        disabled={!plans.next_page}
                    >
                        <ChevronRightIcon />
                    </IconButton>
                </Stack>
            )}

            {/* Results list */}
            <List disablePadding>
                {hasPlans && plans.plans.map((plan: Plan, index: number) => (
                    <PlanItem key={plan.start_time + index} plan={plan} onClick={() => onSelect(plan, index)} />
                ))}
            </List>
        </Box>
    )
}

function PlanItem({ plan, onClick }: { plan: Plan, onClick: () => void }) {
    const { t } = useTranslation()
    const start = DateTime.fromMillis(plan.start_time, { zone: 'Europe/Ljubljana' }).toFormat('HH:mm')
    const end = DateTime.fromMillis(plan.end_time, { zone: 'Europe/Ljubljana' }).toFormat('HH:mm')
    const duration = formatDuration(plan.end_time - plan.start_time)
    const transfers = plan.legs.filter(l => l.mode !== 'WALK').length - 1

    return (
        <ListItemButton
            onClick={onClick}
            sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', flexDirection: 'column', alignItems: 'stretch', gap: 1 }}
        >
            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                <Typography variant="body1" fontWeight={600}>
                    {start}
                    <Typography component="span" color="text.secondary" sx={{ mx: 0.5 }}>–</Typography>
                    {end}
                </Typography>
                <Stack direction="row" alignItems="center" gap={0.5}>
                    <AccessTimeIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                        {duration}
                        {transfers > 0 ? ` · ${t('{{count}} transfers', { count: transfers })}` : ''}
                    </Typography>
                </Stack>
            </Stack>
            <PlanDisplay plan={plan} />
        </ListItemButton>
    )
}
